import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";
import { ROLE_LABEL, useAuth } from "../context/AuthContext";

const ROLE_BADGE = {
  admin: "bg-yellow-100 text-yellow-800 border-yellow-200",
  staff_gudang: "bg-blue-100 text-blue-800 border-blue-200",
  staff_sales: "bg-emerald-100 text-emerald-800 border-emerald-200",
  staff_purchasing: "bg-orange-100 text-orange-800 border-orange-200",
  staff_exim: "bg-cyan-100 text-cyan-800 border-cyan-200",
  staff_personalia: "bg-purple-100 text-purple-800 border-purple-200",
  staff_finance: "bg-indigo-100 text-indigo-800 border-indigo-200",
};

const EMPTY = { employee_id: "", password: "", full_name: "", email: "", role: "staff_sales", active: true };

export default function UserManagement() {
  const { user: current } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...u, password: "" }); setEditingId(u.id); setOpen(true); };

  const save = async () => {
    try {
      if (editingId) {
        const payload = { full_name: form.full_name, email: form.email, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editingId}`, payload);
      } else {
        if (!form.employee_id || !form.password) return toast.error("Employee ID & Password wajib");
        await api.post("/users", form);
      }
      toast.success("User tersimpan");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus user ini?")) return;
    try { await api.delete(`/users/${id}`); toast.success("User dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Administrasi</div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">User Management</h1>
          <p className="text-sm text-slate-500 mt-1">Kelola akses karyawan berdasarkan role: Admin, Gudang, Sales, Purchasing, Ekspor-Impor, Personalia, Finance.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={openNew} className="bg-navy-900 hover:bg-navy-800 text-white" data-testid="user-add-btn"><Plus size={16} className="mr-2" />Tambah User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit User" : "User Baru"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>ID Karyawan</Label><Input data-testid="user-employee-id-input" className="font-mono" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value.toUpperCase() })} disabled={!!editingId} /></div>
              <div><Label>Nama Lengkap</Label><Input data-testid="user-fullname-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin / Owner</SelectItem>
                    <SelectItem value="staff_gudang">Staff Gudang</SelectItem>
                    <SelectItem value="staff_sales">Staff Sales</SelectItem>
                    <SelectItem value="staff_purchasing">Staff Purchasing</SelectItem>
                    <SelectItem value="staff_exim">Staff Ekspor-Impor</SelectItem>
                    <SelectItem value="staff_personalia">Staff Personalia</SelectItem>
                    <SelectItem value="staff_finance">Staff Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Password {editingId && <span className="text-xs text-slate-400">(kosongkan jika tidak diubah)</span>}</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div><Label>Aktif</Label><div className="text-xs text-slate-500">Nonaktifkan untuk memblokir login</div></div>
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-active-switch" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button className="bg-navy-900 hover:bg-navy-800 text-white" onClick={save} data-testid="user-save-btn">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="card-elevated"><CardContent className="p-4">
        <Table data-testid="users-table">
          <TableHeader><TableRow><TableHead>ID Karyawan</TableHead><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.employee_id}`}>
                <TableCell className="font-mono text-xs font-semibold">{u.employee_id}</TableCell>
                <TableCell>{u.full_name}</TableCell>
                <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                <TableCell><Badge className={ROLE_BADGE[u.role]}>{ROLE_LABEL[u.role]}</Badge></TableCell>
                <TableCell>{u.active ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100">Aktif</Badge> : <Badge variant="outline">Nonaktif</Badge>}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(u)} data-testid={`user-edit-${u.employee_id}`}><Pencil size={14} /></Button>
                  {u.id !== current.id && <Button size="sm" variant="ghost" onClick={() => remove(u.id)} data-testid={`user-delete-${u.employee_id}`}><Trash2 size={14} className="text-red-500" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400">Belum ada user</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
    </div>
  );
}
